import {
  BillingCycleRenewalConstants,
  CardReplacementConstants,
  ReversedRenewalConstants,
} from "src/constants";
import { debugLog } from "./debugLog";

/**
 * Checks if current checkout is a card replacement scenario
 *
 * Card replacement occurs when:
 * 1. Order status is "FUTURE" (scheduled payment)
 * 2. isPaymentScheduled flag is true
 *
 * @param checkout - The checkout data object
 * @returns true if card replacement scenario, false otherwise
 *
 * @example
 * const checkout = { status: "FUTURE", isPaymentScheduled: true };
 * const isCardReplacement = isCardReplacementScenario(checkout);
 * // Returns: true
 */
export const isCardReplacementScenario = (
  checkout: CheckoutType | undefined,
): boolean => {
  if (!CardReplacementConstants.ENABLED) {
    return false;
  }

  if (!checkout) {
    return false;
  }

  const { status, isPaymentScheduled } = checkout;

  const isCardReplacement =
    status === CardReplacementConstants.STATUS && isPaymentScheduled === true;

  if (isCardReplacement) {
    debugLog({
      message: `${CardReplacementConstants.DEBUG_LABEL}: Card replacement detected`,
      status,
      isPaymentScheduled,
    });
  }

  return isCardReplacement;
};

/**
 * Checks if current checkout is a billing cycle renewal scenario
 *
 * Billing cycle renewal occurs when:
 * 1. Order status is "CANCELED_BY_GATEWAY" or "CANCELED_BY_GATEWAY_DEFINITIVE"
 * 2. Billing cycle > 1 (not first payment)
 *
 * @param checkout - The checkout data object
 * @returns true if billing cycle renewal scenario, false otherwise
 *
 * @example
 * const checkout = {
 *   status: "CANCELED_BY_GATEWAY",
 *   subscriptionBillingCycle: { billingCycle: 2 }
 * };
 * const isRenewal = isBillingCycleRenewalScenario(checkout);
 * // Returns: true
 */
export const isBillingCycleRenewalScenario = (
  checkout: CheckoutType | undefined,
): boolean => {
  if (!BillingCycleRenewalConstants.ENABLED) {
    return false;
  }

  if (!checkout) {
    return false;
  }

  const { status, subscriptionBillingCycle } = checkout;

  const isBillingCycleRenewal =
    BillingCycleRenewalConstants.STATUSES.includes(status) &&
    subscriptionBillingCycle?.billingCycle >
      BillingCycleRenewalConstants.MIN_CYCLE;

  if (isBillingCycleRenewal) {
    debugLog({
      message: `${BillingCycleRenewalConstants.DEBUG_LABEL}: Billing cycle renewal detected`,
      status,
      billingCycle: subscriptionBillingCycle?.billingCycle,
    });
  }

  return isBillingCycleRenewal;
};

/**
 * Checks if current checkout is a reversed renewal scenario
 *
 * Reversed renewal occurs when:
 * 1. Order status is "REVERSED"
 * 2. Billing cycle > 1 (not first payment)
 *
 * @param checkout - The checkout data object
 * @returns true if reversed renewal scenario, false otherwise
 *
 * @example
 * const checkout = {
 *   status: "REVERSED",
 *   subscriptionBillingCycle: { billingCycle: 2 }
 * };
 * const isReversed = isReversedRenewalScenario(checkout);
 * // Returns: true
 */
export const isReversedRenewalScenario = (
  checkout: CheckoutType | undefined,
): boolean => {
  if (!ReversedRenewalConstants.ENABLED) {
    return false;
  }

  if (!checkout) {
    return false;
  }

  const { status, subscriptionBillingCycle } = checkout;

  const isReversedRenewal =
    status === ReversedRenewalConstants.STATUS &&
    subscriptionBillingCycle?.billingCycle > ReversedRenewalConstants.MIN_CYCLE;

  if (isReversedRenewal) {
    debugLog({
      message: `${ReversedRenewalConstants.DEBUG_LABEL}: Reversed renewal detected`,
      status,
      billingCycle: subscriptionBillingCycle?.billingCycle,
    });
  }

  return isReversedRenewal;
};

/**
 * Checks if installments are required for current checkout
 *
 * Installments are NOT required in these scenarios:
 * - Card replacement (status=FUTURE, isPaymentScheduled=true)
 * - Billing cycle renewal (status=CANCELED_BY_GATEWAY, billingCycle > 1)
 * - Reversed renewal (status=REVERSED, billingCycle > 1)
 *
 * @param checkout - The checkout data object
 * @returns true if installments required, false otherwise
 */
export const isInstallmentRequired = (
  checkout: CheckoutType | undefined,
): boolean => {
  return (
    !isCardReplacementScenario(checkout) &&
    !isBillingCycleRenewalScenario(checkout) &&
    !isReversedRenewalScenario(checkout)
  );
};

export const isLifeType = (checkout: CheckoutType | undefined): boolean => {
  const insuranceType = checkout?.insurance?.type?.name;
  const subscriptionType =
    checkout?.subscriptionBillingCycle?.subscription?.type;

  return [insuranceType, subscriptionType].some(
    (type) => type?.toUpperCase() === "LIFE",
  );
};

export const isAutoType = (checkout: CheckoutType | undefined): boolean => {
  const insuranceType = checkout?.insurance?.type?.name;
  const subscriptionType =
    checkout?.subscriptionBillingCycle?.subscription?.type;

  return [insuranceType, subscriptionType].some(
    (type) => type?.toUpperCase() === "AUTO",
  );
};

const CheckoutHelper = {
  isCardReplacementScenario,
  isBillingCycleRenewalScenario,
  isReversedRenewalScenario,
  isInstallmentRequired,
  isLifeType,
  isAutoType,
};

export default CheckoutHelper;
